import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Container, Row, Col, Card, Table, Button, Badge, Form, InputGroup, Alert, Dropdown, Modal, Toast, ToastContainer, Spinner, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { Plus, Envelope, Filter, Search, CheckCircle, XCircle, ArrowClockwise, Trash, ThreeDotsVertical, Gear, QuestionCircle, Copy, InfoCircle, ArrowUp, ArrowDown, ArrowDownUp } from 'react-bootstrap-icons';
import {
  useGetEmailAccountsQuery,
  useDeleteEmailAccountMutation,
  useUpdateEmailAccountMutation,
  useUploadEmailAccountAvatarMutation,
  useGetDNSSetupQuery,
  useGetDomainsQuery,
  useLazyGetDomainRoutingPolicyQuery,
  useGetUserPreferencesQuery,
} from '../services/api';
import { useAccount } from '../contexts/AccountContext';
import { useUserPreferencesQueryArg } from '../hooks/useTenantIdForPreferences';
import EMMSectionHeader from './EMMSectionHeader';
import AddEmailAccount from './AddEmailAccount';
import { Offcanvas } from 'react-bootstrap';
import {
  getDateKeyInTimezone,
  getTenantTimezoneFromPreferencesData,
  toLocaleDateStringInTimezone,
} from '../utils/tenantTimezone';

// TypeScript interfaces
interface EmailAccount {
  id: number;
  email: string;
  username: string;
  domain: string;
  status: string;
  effectiveReadyToSend?: boolean;
  verificationStatus?: string;
  verifiedDate: string | null;
  createdAt: string | null;
  lastUsed: string | null;
  sendCount: number;
  bounceRate: number;
  avatar?: string | null; // Optional avatar URL
  hideFromInbox?: boolean;
  hideFromInboxCount?: boolean;
}

interface Domain {
  id: number;
  domain: string;
}

interface DomainStatusSnapshot {
  domain: string;
  status?: string;
  lifecycleStatus?: string;
  authProvider?: string | null;
  dkimProvider?: string | null;
  spfProvider?: string | null;
  authVerifiedAt?: string | null;
  dkimStatus?: string;
  spfStatus?: string;
  dmarcStatus?: string;
  outboundConfigured?: boolean;
  outboundNeedsAttention?: boolean;
  coldOutreachPrimaryPoolId?: string | null;
  transactionalPrimaryPoolId?: string | null;
  newsletterPrimaryPoolId?: string | null;
}

interface DomainRoutingPolicy {
  routes?: {
    cold_outreach?: { primaryPoolId?: string | null };
    transactional?: { primaryPoolId?: string | null };
    newsletter?: { primaryPoolId?: string | null };
  };
}

interface ApiEmailAccount {
  fullEmail?: string;
  email: string;
  domain: string;
  status: string;
  createdAt?: string | null;
  createdDate?: string | null;
  lastUsed?: string;
  sentCount?: number;
  bounceRate?: number;
  verificationStatus?: string;
  verifiedDate?: string | null;
  hideFromInbox?: boolean;
  hideFromInboxCount?: boolean;
}

interface ApiResponse {
  data?: {
    emailAccounts?: ApiEmailAccount[];
    sendCountYear?: number;
  };
}

type DeleteMode = 'single' | 'bulk';

function normalizeAuthStatus(status: unknown): string {
  return String(status || '').trim().toLowerCase();
}

function isVerifiedAuthStatus(status: unknown): boolean {
  const normalized = normalizeAuthStatus(status);
  return normalized === 'verified' || normalized === 'success' || normalized === 'configured' || normalized === 'pass';
}

function isLifecycleReady(status: unknown): boolean {
  const normalized = String(status || '').trim().toLowerCase();
  return normalized === 'connected' || normalized === 'verified' || normalized === 'active';
}

function isAuthenticationConfigured(domain: DomainStatusSnapshot | null | undefined): boolean {
  if (!domain) return false;
  const provider = String(domain.authProvider || domain.dkimProvider || domain.spfProvider || '').trim().toLowerCase();
  if (domain.authVerifiedAt && provider) return true;
  return (
    isVerifiedAuthStatus(domain.dkimStatus) &&
    isVerifiedAuthStatus(domain.spfStatus) &&
    isVerifiedAuthStatus(domain.dmarcStatus)
  );
}

function hasAnyPrimaryPoolRoute(policy: DomainRoutingPolicy | null | undefined): boolean {
  if (!policy?.routes) return false;
  const routes = policy.routes;
  return Boolean(
    routes.cold_outreach?.primaryPoolId ||
      routes.transactional?.primaryPoolId ||
      routes.newsletter?.primaryPoolId
  );
}

// DNS Section Component for Email Account Settings
const EmailAccountDNSSection: React.FC<{ domain: string; email: string }> = ({ domain, email }) => {
  const { data: dnsData, isLoading: dnsLoading, error: dnsError } = useGetDNSSetupQuery(domain);
  const [copiedRecord, setCopiedRecord] = useState<string>('');

  const handleCopyRecord = (record: string) => {
    navigator.clipboard.writeText(record);
    setCopiedRecord(record);
    setTimeout(() => setCopiedRecord(''), 2000);
  };

  if (dnsLoading) {
    return (
      <div className="mb-3">
        <h6 className="mb-3">DNS Records</h6>
        <div className="text-center py-3">
          <Spinner animation="border" size="sm" className="me-2" />
          <span className="text-muted">Loading DNS records...</span>
        </div>
      </div>
    );
  }

  if (dnsError) {
    return (
      <div className="mb-3">
        <h6 className="mb-3">DNS Records</h6>
        <Alert variant="warning" className="mb-0">
          Unable to load DNS records for this domain.
        </Alert>
      </div>
    );
  }

  const dnsRecords = dnsData?.data || {};
  const hasRecords = dnsRecords.verification || dnsRecords.spf || dnsRecords.dkim || dnsRecords.dmarc || dnsRecords.mailFrom;

  if (!hasRecords) {
    return (
      <div className="mb-3">
        <h6 className="mb-3">DNS Records</h6>
        <Alert variant="info" className="mb-0">
          No DNS records configured for this domain yet.
        </Alert>
      </div>
    );
  }

  return (
    <div className="mb-3">
      <h6 className="mb-3">DNS Records</h6>
      <div className="small">
        <p className="text-muted mb-3">
          DNS records configured for <strong>{domain}</strong> to enable email sending:
        </p>
        
        {/* SES Verification Record */}
        {dnsRecords.verification && (
          <div className="mb-3">
            <div className="d-flex align-items-center mb-2">
              <strong className="me-2">SES Verification:</strong>
              {dnsRecords.verification.verified ? (
                <Badge bg="success" className="ms-2">
                  <CheckCircle size={12} className="me-1" />
                  Verified
                </Badge>
              ) : (
                <Badge bg="warning" className="ms-2">Pending</Badge>
              )}
            </div>
            {dnsRecords.verification.record && (
              <div className="bg-light p-2 rounded d-flex align-items-center justify-content-between">
                <code className="small">{dnsRecords.verification.record}</code>
                <Button
                  size="sm"
                  variant="outline-secondary"
                  onClick={() => handleCopyRecord(dnsRecords.verification.record)}
                  className="ms-2"
                >
                  {copiedRecord === dnsRecords.verification.record ? (
                    <CheckCircle size={14} />
                  ) : (
                    <Copy size={14} />
                  )}
                </Button>
              </div>
            )}
          </div>
        )}

        {/* SPF Record */}
        {dnsRecords.spf && (
          <div className="mb-3">
            <div className="d-flex align-items-center mb-2">
              <strong className="me-2">SPF:</strong>
              {dnsRecords.spf.verified ? (
                <Badge bg="success" className="ms-2">
                  <CheckCircle size={12} className="me-1" />
                  Verified
                </Badge>
              ) : (
                <Badge bg="warning" className="ms-2">Pending</Badge>
              )}
            </div>
            {dnsRecords.spf.record && (
              <div className="bg-light p-2 rounded d-flex align-items-center justify-content-between">
                <code className="small">{dnsRecords.spf.record}</code>
                <Button
                  size="sm"
                  variant="outline-secondary"
                  onClick={() => handleCopyRecord(dnsRecords.spf.record)}
                  className="ms-2"
                >
                  {copiedRecord === dnsRecords.spf.record ? (
                    <CheckCircle size={14} />
                  ) : (
                    <Copy size={14} />
                  )}
                </Button>
              </div>
            )}
          </div>
        )}

        {/* DKIM Records */}
        {dnsRecords.dkim && dnsRecords.dkim.tokens && dnsRecords.dkim.tokens.length > 0 && (
          <div className="mb-3">
            <div className="d-flex align-items-center mb-2">
              <strong className="me-2">DKIM:</strong>
              {dnsRecords.dkim.verified ? (
                <Badge bg="success" className="ms-2">
                  <CheckCircle size={12} className="me-1" />
                  Verified
                </Badge>
              ) : (
                <Badge bg="warning" className="ms-2">Pending</Badge>
              )}
            </div>
            {dnsRecords.dkim.tokens.map((token: any, index: number) => (
              <div key={index} className="bg-light p-2 rounded d-flex align-items-center justify-content-between mb-2">
                <code className="small">{token.record}</code>
                <Button
                  size="sm"
                  variant="outline-secondary"
                  onClick={() => handleCopyRecord(token.record)}
                  className="ms-2"
                >
                  {copiedRecord === token.record ? (
                    <CheckCircle size={14} />
                  ) : (
                    <Copy size={14} />
                  )}
                </Button>
              </div>
            ))}
          </div>
        )}

        {/* DMARC Record */}
        {dnsRecords.dmarc && (
          <div className="mb-3">
            <div className="d-flex align-items-center mb-2">
              <strong className="me-2">DMARC:</strong>
              {dnsRecords.dmarc.verified ? (
                <Badge bg="success" className="ms-2">
                  <CheckCircle size={12} className="me-1" />
                  Verified
                </Badge>
              ) : (
                <Badge bg="warning" className="ms-2">Pending</Badge>
              )}
            </div>
            {dnsRecords.dmarc.record && (
              <div className="bg-light p-2 rounded d-flex align-items-center justify-content-between">
                <code className="small">{dnsRecords.dmarc.record}</code>
                <Button
                  size="sm"
                  variant="outline-secondary"
                  onClick={() => handleCopyRecord(dnsRecords.dmarc.record)}
                  className="ms-2"
                >
                  {copiedRecord === dnsRecords.dmarc.record ? (
                    <CheckCircle size={14} />
                  ) : (
                    <Copy size={14} />
                  )}
                </Button>
              </div>
            )}
          </div>
        )}

        {/* MAIL FROM Records */}
        {dnsRecords.mailFrom && (
          <div className="mb-3">
            <div className="d-flex align-items-center mb-2">
              <strong className="me-2">MAIL FROM:</strong>
              {dnsRecords.mailFrom.verified ? (
                <Badge bg="success" className="ms-2">
                  <CheckCircle size={12} className="me-1" />
                  Verified
                </Badge>
              ) : (
                <Badge bg="warning" className="ms-2">Pending</Badge>
              )}
            </div>
            {dnsRecords.mailFrom.mx && (
              <div className="bg-light p-2 rounded d-flex align-items-center justify-content-between mb-2">
                <code className="small">{dnsRecords.mailFrom.mx.record}</code>
                <Button
                  size="sm"
                  variant="outline-secondary"
                  onClick={() => handleCopyRecord(dnsRecords.mailFrom.mx.record)}
                  className="ms-2"
                >
                  {copiedRecord === dnsRecords.mailFrom.mx.record ? (
                    <CheckCircle size={14} />
                  ) : (
                    <Copy size={14} />
                  )}
                </Button>
              </div>
            )}
            {dnsRecords.mailFrom.txt && (
              <div className="bg-light p-2 rounded d-flex align-items-center justify-content-between">
                <code className="small">{dnsRecords.mailFrom.txt.record}</code>
                <Button
                  size="sm"
                  variant="outline-secondary"
                  onClick={() => handleCopyRecord(dnsRecords.mailFrom.txt.record)}
                  className="ms-2"
                >
                  {copiedRecord === dnsRecords.mailFrom.txt.record ? (
                    <CheckCircle size={14} />
                  ) : (
                    <Copy size={14} />
                  )}
                </Button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

const EmailAccounts: React.FC = () => {
  // Component mounted - minimal logging
  
  const accountContext = useAccount();
  const { selectedAccount, isAllAccountsSelected, isLoading } = accountContext;
  
  // Log every time the component renders
  // Component rendered - minimal logging
  const [filteredAccounts, setFilteredAccounts] = useState<EmailAccount[]>([]);
  const [selectedDomain, setSelectedDomain] = useState<string>('');
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [showAddModal, setShowAddModal] = useState<boolean>(false);
  const [selectedAccounts, setSelectedAccounts] = useState<Set<number>>(new Set());
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
  const [deleteMode, setDeleteMode] = useState<DeleteMode>('single');
  const [showHelp, setShowHelp] = useState<boolean>(false);
  const [showSettingsModal, setShowSettingsModal] = useState<boolean>(false);
  const [editingAccount, setEditingAccount] = useState<EmailAccount | null>(null);
  const [avatarPreview, setAvatarPreview] = useState<string | null>(null);
  const [avatarFile, setAvatarFile] = useState<File | null>(null);
  const [hideFromInbox, setHideFromInbox] = useState<boolean>(false);
  const [hideFromInboxCount, setHideFromInboxCount] = useState<boolean>(false);
  const [isSavingSettings, setIsSavingSettings] = useState<boolean>(false);
  const saveSettingsInFlightRef = useRef<boolean>(false);
  const [showSuccessToast, setShowSuccessToast] = useState<boolean>(false);
  const [showErrorToast, setShowErrorToast] = useState<boolean>(false);
  const [toastMessage, setToastMessage] = useState<string>('');
  const [autoRefresh, setAutoRefresh] = useState<boolean>(false);
  const [failedAvatars, setFailedAvatars] = useState<Set<number>>(new Set());
  type SortColumn = 'email' | 'domain' | 'status' | 'sendCount' | 'bounceRate' | 'createdAt' | 'lastUsed' | 'actions';
  const [sortBy, setSortBy] = useState<SortColumn | null>(null);
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');

  const { 
    data: emailAccountsData, 
    error, 
    isLoading: loading,
    isFetching: isRefreshing,
    refetch: loadEmailAccounts 
  } = useGetEmailAccountsQuery(undefined, {
    // Enable refetching when account changes
    refetchOnMountOrArgChange: true,
    refetchOnFocus: false,
    refetchOnReconnect: false
  });
  const userPreferencesQueryArg = useUserPreferencesQueryArg();
  const { data: userPreferencesData } = useGetUserPreferencesQuery(userPreferencesQueryArg);
  const tenantTimezone = useMemo(
    () => getTenantTimezoneFromPreferencesData(userPreferencesData),
    [userPreferencesData]
  );

  // Get domains to check if domain is verified (allows emails to receive even if individual email is pending)
  const { data: domainsData } = useGetDomainsQuery(undefined, {
    refetchOnMountOrArgChange: false,
    refetchOnFocus: false,
    refetchOnReconnect: false
  });
  const [triggerGetDomainRoutingPolicy] = useLazyGetDomainRoutingPolicyQuery();
  const [domainRoutingByDomain, setDomainRoutingByDomain] = useState<Record<string, DomainRoutingPolicy>>({});
  const [outboundPrefetchDone, setOutboundPrefetchDone] = useState(false);

  const domainStatusByDomain = useMemo((): Record<string, DomainStatusSnapshot> => {
    const records = ((domainsData as any)?.data?.domains || []) as DomainStatusSnapshot[];
    const next: Record<string, DomainStatusSnapshot> = {};
    for (const domain of records) {
      const domainName = String(domain?.domain || '').trim().toLowerCase();
      if (!domainName) continue;
      next[domainName] = domain;
    }
    return next;
  }, [domainsData]);

  const domainListKey = useMemo(
    () => JSON.stringify(Object.keys(domainStatusByDomain).sort()),
    [domainStatusByDomain]
  );

  useEffect(() => {
    if (!domainListKey || domainListKey === '[]') {
      setOutboundPrefetchDone(true);
      return;
    }

    let cancelled = false;
    const domains = JSON.parse(domainListKey) as string[];
    const PREFETCH_CONCURRENCY = 5;
    setOutboundPrefetchDone(false);

    const runBatch = (batch: string[]) =>
      Promise.allSettled(
        batch.map(async (domain) => {
          try {
            const res = await triggerGetDomainRoutingPolicy(domain, true).unwrap();
            return {
              domain,
              policy: (res as any)?.data?.policy ?? null,
            };
          } catch (err: any) {
            console.warn('[email-accounts:outbound-prefetch]', domain, err?.message || err);
            return { domain, policy: null };
          }
        })
      );

    (async () => {
      try {
        const next: Record<string, DomainRoutingPolicy> = {};
        for (let i = 0; i < domains.length; i += PREFETCH_CONCURRENCY) {
          if (cancelled) break;
          const batch = domains.slice(i, i + PREFETCH_CONCURRENCY);
          const results = await runBatch(batch);
          results.forEach((result) => {
            if (result.status !== 'fulfilled') return;
            const { domain, policy } = result.value || {};
            if (!domain || !hasAnyPrimaryPoolRoute(policy)) return;
            next[domain] = policy as DomainRoutingPolicy;
          });
        }
        if (!cancelled) {
          setDomainRoutingByDomain((prev) => ({ ...prev, ...next }));
        }
      } finally {
        if (!cancelled) setOutboundPrefetchDone(true);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [domainListKey, triggerGetDomainRoutingPolicy]);

  // Force refetch when selected account changes
  useEffect(() => {
    console.log('🔄 EmailAccounts - useEffect triggered, selectedAccount:', selectedAccount);
    if (selectedAccount?.id) {
      console.log('🔄 EmailAccounts - Account changed, refetching:', selectedAccount.id);
      loadEmailAccounts();
    }
  }, [selectedAccount?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const [deleteEmailAccount, { isLoading: isDeleting }] = useDeleteEmailAccountMutation();
  const [uploadEmailAccountAvatar] = useUploadEmailAccountAvatarMutation();
  const [updateEmailAccount] = useUpdateEmailAccountMutation();
  const sendCountYear = Number((emailAccountsData as ApiResponse | undefined)?.data?.sendCountYear) || new Date().getUTCFullYear();

  // Transform API data to our email account format with account filtering - memoized to prevent infinite loops
  // Only use real API data - no mock data
  const emailAccounts = useMemo((): EmailAccount[] => {
    const apiData = emailAccountsData as ApiResponse;
    const apiAccounts = apiData?.data?.emailAccounts || [];
    
    // Debug logging
    console.log('🔍 EmailAccounts - selectedAccount:', selectedAccount);
    console.log('🔍 EmailAccounts - isAllAccountsSelected:', isAllAccountsSelected);
    console.log('🔍 EmailAccounts - API accounts:', apiAccounts);
    
    // Filter accounts by selected account domains if a specific account is selected
    let filteredAccounts = apiAccounts;
    if (!isAllAccountsSelected && selectedAccount.id !== 'all' && selectedAccount.domains && selectedAccount.domains.length > 0) {
      // Filter by domains that belong to the selected account
      // selectedAccount.domains is an array of strings, not objects
      const accountDomains = Array.isArray(selectedAccount.domains) 
        ? selectedAccount.domains 
        : [];
      
      console.log('🔍 EmailAccounts - Filtering by account domains:', accountDomains);
      
      filteredAccounts = apiAccounts.filter((account: ApiEmailAccount) => {
        const accountDomain = account.domain;
        const matches = accountDomains.includes(accountDomain);
        console.log(`🔍 EmailAccounts - ${accountDomain} matches: ${matches}`);
        return matches;
      });
      
      console.log('🔍 EmailAccounts - Filtered accounts:', filteredAccounts);
    } else {
      console.log('🔍 EmailAccounts - Showing all accounts (no filtering)');
    }
    
    const result = filteredAccounts.map((account: ApiEmailAccount, index: number) => {
      const fullEmail = account.fullEmail || `${account.email}@${account.domain}`;
      const domainName = String(account.domain || '').trim().toLowerCase();
      const domainSnapshot = domainStatusByDomain[domainName];
      const routingPolicy = domainRoutingByDomain[domainName];

      const rawStatus = String(account.status || '').trim().toLowerCase();
      const rawVerificationStatus = String(account.verificationStatus || '').trim().toLowerCase();
      const baseReadyToSend =
        rawVerificationStatus === 'success' ||
        rawStatus === 'verified' ||
        rawStatus === 'active' ||
        rawStatus === 'success';
      const baseFailed = rawStatus === 'failed';

      const lifecycleReady = isLifecycleReady(domainSnapshot?.lifecycleStatus || domainSnapshot?.status);
      const authConfigured = domainSnapshot ? (isAuthenticationConfigured(domainSnapshot) || lifecycleReady) : false;
      const outboundConfiguredFromApi = Boolean(
        domainSnapshot?.outboundConfigured === true ||
          String(domainSnapshot?.coldOutreachPrimaryPoolId || '').trim() ||
          String(domainSnapshot?.transactionalPrimaryPoolId || '').trim() ||
          String(domainSnapshot?.newsletterPrimaryPoolId || '').trim()
      );
      const outboundConfiguredFromPolicy = hasAnyPrimaryPoolRoute(routingPolicy);
      const outboundNeedsAttention = domainSnapshot?.outboundNeedsAttention === true;
      const outboundConfigured = !outboundNeedsAttention && (outboundConfiguredFromApi || outboundConfiguredFromPolicy);

      // While outbound policy prefetch is running, avoid regressing to "Pending"
      // if we still have no domain routing signal yet.
      const outboundSignalKnown = outboundConfiguredFromApi || outboundConfiguredFromPolicy || outboundPrefetchDone;
      const effectiveReadyToSend =
        baseReadyToSend || (authConfigured && outboundConfigured && outboundSignalKnown);

      const effectiveStatus = effectiveReadyToSend ? 'active' : baseFailed ? 'failed' : 'pending';

      return {
        id: index + 1,
        email: fullEmail,
        username: account.email,
        domain: account.domain,
        status: effectiveStatus,
        effectiveReadyToSend,
        verificationStatus: account.verificationStatus,
        verifiedDate:
          account.verifiedDate ||
          (effectiveReadyToSend ? (getDateKeyInTimezone(new Date(), tenantTimezone) || null) : null),
        createdAt: account.createdAt || account.createdDate || null,
        lastUsed: account.lastUsed || null,
        sendCount: account.sentCount || 0,
        bounceRate: account.bounceRate || 0,
        avatar: (account as any).avatar || null,
        hideFromInbox: !!(account as any).hideFromInbox,
        hideFromInboxCount: !!(account as any).hideFromInboxCount,
      };
    });
    
    return result;
  }, [
    emailAccountsData,
    selectedAccount?.id,
    isAllAccountsSelected,
    domainStatusByDomain,
    domainRoutingByDomain,
    outboundPrefetchDone,
    tenantTimezone,
  ]);

  // Get unique domains from email addresses - memoized to prevent infinite loops
  const domains = useMemo((): Domain[] => {
    const uniqueDomains = Array.from(
      new Set(
        emailAccounts
          .map((account) => String(account.domain || '').trim())
          .filter(Boolean)
      )
    ).sort((a, b) =>
      a.localeCompare(b, undefined, { sensitivity: 'base' })
    );

    return uniqueDomains.map((domain: string, index: number) => ({ id: index + 1, domain }));
  }, [emailAccounts]);

  // Filter accounts based on domain and search term
  useEffect(() => {
    let filtered = emailAccounts;

    if (selectedDomain) {
      filtered = filtered.filter(account => account.domain === selectedDomain);
    }

    if (searchTerm) {
      filtered = filtered.filter(account => 
        account.email.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    setFilteredAccounts(filtered);
  }, [selectedDomain, searchTerm, emailAccounts]);

  // Sorted list for table
  const sortedAccounts = useMemo(() => {
    if (!sortBy) {
      return [...filteredAccounts].sort((a, b) =>
        String(a.email || '').toLowerCase().localeCompare(String(b.email || '').toLowerCase())
      );
    }
    const dir = sortDir === 'asc' ? 1 : -1;
    return [...filteredAccounts].sort((a, b) => {
      let cmp = 0;
      if (sortBy === 'email' || sortBy === 'domain') {
        const aVal = (sortBy === 'email' ? a.email : a.domain)?.toLowerCase() ?? '';
        const bVal = (sortBy === 'email' ? b.email : b.domain)?.toLowerCase() ?? '';
        cmp = aVal.localeCompare(bVal);
      } else if (sortBy === 'status') {
        const aVal = (a.status ?? '').toLowerCase();
        const bVal = (b.status ?? '').toLowerCase();
        cmp = aVal.localeCompare(bVal);
      } else if (sortBy === 'sendCount') {
        cmp = (a.sendCount ?? 0) - (b.sendCount ?? 0);
      } else if (sortBy === 'bounceRate') {
        cmp = (a.bounceRate ?? 0) - (b.bounceRate ?? 0);
      } else if (sortBy === 'createdAt') {
        const aTime = a.createdAt ? new Date(a.createdAt).getTime() : 0;
        const bTime = b.createdAt ? new Date(b.createdAt).getTime() : 0;
        cmp = aTime - bTime; // null/unknown (0) sorts first in asc, last in desc
      } else if (sortBy === 'lastUsed') {
        const aTime = a.lastUsed ? new Date(a.lastUsed).getTime() : 0;
        const bTime = b.lastUsed ? new Date(b.lastUsed).getTime() : 0;
        cmp = aTime - bTime; // null/never (0) sorts first in asc, last in desc
      } else if (sortBy === 'actions') {
        cmp = a.id - b.id;
      }
      return dir * cmp;
    });
  }, [filteredAccounts, sortBy, sortDir]);

  const handleSort = (column: SortColumn) => {
    if (sortBy === column) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortDir('asc');
    }
  };

  const isAccountReadyToSend = (account: EmailAccount | null | undefined): boolean => {
    if (!account) return false;
    if (account.effectiveReadyToSend === true) return true;
    const verificationStatus = String(account.verificationStatus || '').trim().toLowerCase();
    const status = String(account.status || '').trim().toLowerCase();
    return (
      verificationStatus === 'success' ||
      status === 'verified' ||
      status === 'active' ||
      status === 'success'
    );
  };

  const getStatusBadge = (account: EmailAccount): React.JSX.Element => {
    // Active = account can send based on current domain auth + outbound route state.
    if (isAccountReadyToSend(account)) {
      return <Badge bg="success"><CheckCircle className="me-1" />Active</Badge>;
    }
    if (String(account.status || '').toLowerCase() === 'failed') {
      return <Badge bg="danger"><XCircle className="me-1" />Failed</Badge>;
    }
    return (
      <Badge bg="warning" title="Configure outbound in Domains → Outbound (DNS + relay credentials).">
        Pending
      </Badge>
    );
  };

  // Capability-based outbound status (no SES). Ready to Send = DNS + SMTP creds in place; otherwise Configure in Domains.
  const getOutboundStatusBadge = (account: EmailAccount): React.JSX.Element => {
    const canSend = isAccountReadyToSend(account);
    if (canSend) {
      return (
        <OverlayTrigger
          placement="top"
          overlay={
            <Tooltip>
              <strong>Ready to Send</strong> — Outbound is configured (DNS + SMTP.com credentials in Domains → Outbound). No verification step.
            </Tooltip>
          }
        >
          <Badge bg="success" style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem', cursor: 'help' }}>
            <CheckCircle size={12} className="me-1" />
            Ready to Send
          </Badge>
        </OverlayTrigger>
      );
    }
    return (
      <OverlayTrigger
        placement="top"
        overlay={
          <Tooltip>
            Set DNS (SPF/DKIM/DMARC) and SMTP.com channel credentials in <strong>Domains → Outbound</strong>. No sender verification step.
          </Tooltip>
        }
      >
        <Badge bg="secondary" style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem', cursor: 'help' }}>
          <InfoCircle size={12} className="me-1" />
          Configure in Domains
        </Badge>
      </OverlayTrigger>
    );
  };

  const handleAddAccount = (): void => {
    setShowAddModal(true);
  };

  const handleAddSuccess = (result: any): void => {
    console.log('Email account created:', result);
    const createdEmail = String(result?.data?.email || result?.email || '').toLowerCase().trim();
    if (createdEmail.startsWith('dmarc@')) {
      updateEmailAccount({ email: createdEmail, hideFromInbox: true })
        .unwrap()
        .then(() => {
          setToastMessage(`${createdEmail} has been automatically hidden from All Inboxes (DMARC collection inbox).`);
          setShowSuccessToast(true);
          setTimeout(() => setShowSuccessToast(false), 5000);
        })
        .catch(() => { /* non-fatal */ });
    }
    loadEmailAccounts().catch((error) => {
      console.error('Error refreshing email accounts:', error);
    });
  };

  const handleRefresh = async (): Promise<void> => {
    console.log('🔄 Refresh button clicked');
    try {
      console.log('🔄 Calling loadEmailAccounts (refetch)...');
      const result = await loadEmailAccounts();
      console.log('🔄 Refetch result:', result);
      
      // Only show success toast if not auto-refreshing (to avoid spam)
      if (!autoRefresh) {
        setToastMessage('Email accounts refreshed successfully');
        setShowSuccessToast(true);
        
        // Auto-hide toast after 3 seconds
        setTimeout(() => {
          setShowSuccessToast(false);
        }, 3000);
      }
      console.log('✅ Email accounts refreshed successfully');
    } catch (error: any) {
      console.error('❌ Error refreshing email accounts:', error);
      const errorMsg = error?.data?.message || error?.message || 'Failed to refresh email accounts. Please try again.';
      setToastMessage(errorMsg);
      setShowErrorToast(true);
      
      // Auto-hide error toast after 5 seconds
      setTimeout(() => {
        setShowErrorToast(false);
      }, 5000);
    }
  };

  // Auto-refresh effect
  useEffect(() => {
    let intervalId: NodeJS.Timeout | null = null;
    
    if (autoRefresh) {
      // Refresh immediately when enabled
      handleRefresh();
      
      // Then refresh every 30 seconds
      intervalId = setInterval(() => {
        handleRefresh();
      }, 30000); // 30 seconds
    }
    
    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [autoRefresh]); // eslint-disable-line react-hooks/exhaustive-deps


  // Selection handlers
  const handleSelectAccount = (accountId: number): void => {
    const newSelected = new Set(selectedAccounts);
    if (newSelected.has(accountId)) {
      newSelected.delete(accountId);
    } else {
      newSelected.add(accountId);
    }
    setSelectedAccounts(newSelected);
  };

  const handleSelectAll = (): void => {
    if (selectedAccounts.size === sortedAccounts.length) {
      setSelectedAccounts(new Set());
    } else {
      setSelectedAccounts(new Set(sortedAccounts.map(account => account.id)));
    }
  };

  const handleDeleteAccount = (accountId: number): void => {
    setDeleteMode('single');
    setSelectedAccounts(new Set([accountId]));
    setShowDeleteModal(true);
  };

  const handleBulkDelete = (): void => {
    if (selectedAccounts.size === 0) return;
    setDeleteMode('bulk');
    setShowDeleteModal(true);
  };

  const confirmDelete = async (): Promise<void> => {
    try {
      const selectedIds = Array.from(selectedAccounts);
      
      // Delete each selected account
      for (const accountId of selectedIds) {
        const account = filteredAccounts.find(acc => acc.id === accountId);
        if (account) {
          // Use the fullEmail as the ID for the API call
          await deleteEmailAccount(account.email).unwrap();
        }
      }
      
      // Refresh the list
      loadEmailAccounts();
      setSelectedAccounts(new Set());
      setShowDeleteModal(false);
      
      // Show success message
      alert(`Successfully deleted ${selectedIds.length} email account(s)`);
    } catch (error: any) {
      console.error('Error deleting accounts:', error);
      const message = String(
        error?.data?.message ||
        error?.error ||
        error?.message ||
        'Failed to delete email accounts'
      );
      alert(message);
    }
  };


  const handleSettings = (accountId: number): void => {
    const account = filteredAccounts.find(acc => acc.id === accountId);
    if (!account) return;
    
    setEditingAccount(account);
    setAvatarPreview((account as any).avatar || null);
    setHideFromInbox(!!(account as any).hideFromInbox);
    setHideFromInboxCount(!!(account as any).hideFromInboxCount);
    setShowSettingsModal(true);
  };

  const handleAvatarChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const file = e.target.files?.[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        setToastMessage('Please select an image file');
        setShowErrorToast(true);
        setTimeout(() => setShowErrorToast(false), 5000);
        return;
      }
      
      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        setToastMessage('Image file size must be less than 5MB');
        setShowErrorToast(true);
        setTimeout(() => setShowErrorToast(false), 5000);
        return;
      }
      
      // Store the file for upload
      setAvatarFile(file);
      
      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setAvatarPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const closeSettingsModal = (): void => {
    if (isSavingSettings) return;
    setShowSettingsModal(false);
    setEditingAccount(null);
    setAvatarPreview(null);
  };

  const handleSaveSettings = async (): Promise<void> => {
    if (!editingAccount || saveSettingsInFlightRef.current) return;
    saveSettingsInFlightRef.current = true;
    setIsSavingSettings(true);
    try {
      if (avatarFile) {
        await uploadEmailAccountAvatar({
          emailId: editingAccount.email,
          file: avatarFile
        }).unwrap();
      }

      await updateEmailAccount({
        email: editingAccount.email,
        hideFromInbox,
        hideFromInboxCount,
      }).unwrap();

      void loadEmailAccounts();

      setToastMessage(
        avatarFile ? 'Settings and profile picture saved.' : 'Settings saved successfully'
      );
      setShowSuccessToast(true);
      setTimeout(() => setShowSuccessToast(false), 3000);

      setShowSettingsModal(false);
      setEditingAccount(null);
      setAvatarPreview(null);
      setAvatarFile(null);
    } catch (error: any) {
      console.error('Error saving settings:', error);
      const errorMsg = error?.data?.message || error?.message || 'Failed to save settings. Please try again.';
      setToastMessage(errorMsg);
      setShowErrorToast(true);
      setTimeout(() => setShowErrorToast(false), 5000);
    } finally {
      saveSettingsInFlightRef.current = false;
      setIsSavingSettings(false);
    }
  };

  const getBounceRateColor = (rate: number): string => {
    if (rate < 2) return 'success';
    if (rate < 5) return 'warning';
    return 'danger';
  };

  if (loading) {
    return (
      <Container className="py-4" style={{ position: 'relative', minHeight: '400px' }}>
        <div 
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(255, 255, 255, 0.8)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            borderRadius: '8px'
          }}
        >
          <div className="text-center">
            <ArrowClockwise size={32} className="text-primary mb-2" style={{ animation: 'spin 1s linear infinite' }} />
            <div className="text-muted">Loading email accounts...</div>
          </div>
        </div>
      </Container>
    );
  }

  return (
    <>
      <EMMSectionHeader
        title="Email Accounts"
        showHelp
        onHelpClick={() => setShowHelp(true)}
      />
      <Container className="py-3" style={{ position: 'relative' }}>
      {/* Action bar - same spot and size as Clients "+ Client" button */}
      <div className="d-flex align-items-center gap-2 mb-3">
        <Button
          variant="primary"
          size="sm"
          className="d-flex align-items-center"
          style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}
          onClick={() => setShowAddModal(true)}
        >
          <Plus className="me-1" size={14} />
          Account
        </Button>
        <span className="text-muted" style={{ fontSize: '0.75rem' }}>
          {filteredAccounts.length} account{filteredAccounts.length !== 1 ? 's' : ''}
        </span>
      </div>
      {/* Loading Overlay */}
      {isLoading && (
        <div 
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(255, 255, 255, 0.8)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            borderRadius: '8px'
          }}
        >
          <div className="text-center">
            <ArrowClockwise size={32} className="text-primary mb-2" style={{ animation: 'spin 1s linear infinite' }} />
            <div className="text-muted">Switching to {selectedAccount.alias}...</div>
          </div>
        </div>
      )}
      <Row className="mb-4">
        <Col>
          <div className="d-flex justify-content-between align-items-center">
            <div>
              <p className="text-muted mb-0">
                {isAllAccountsSelected 
                  ? 'View your email accounts and monitor performance' 
                  : `Viewing email accounts for ${selectedAccount.name}`
                }
              </p>
            </div>
            {selectedAccounts.size > 0 && (
              <div className="d-flex gap-2 align-items-center">
                <Dropdown>
                  <Dropdown.Toggle variant="outline-danger" id="bulk-actions-dropdown">
                    <ThreeDotsVertical className="me-1" />
                    Bulk Actions ({selectedAccounts.size})
                  </Dropdown.Toggle>
                  <Dropdown.Menu>
                    <Dropdown.Item onClick={handleBulkDelete}>
                      <Trash className="me-2" />
                      Delete Selected ({selectedAccounts.size})
                    </Dropdown.Item>
                  </Dropdown.Menu>
                </Dropdown>
              </div>
            )}
          </div>
        </Col>
      </Row>

      {error && (
        <Alert variant="danger" className="mb-4">
          {'message' in error ? error.message : 'Failed to load email accounts. Please check your backend connection.'}
        </Alert>
      )}

      {/* Filters */}
      <Row className="mb-4">
        <Col>
          <Card>
            <Card.Body>
              <Row>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Filter by Domain</Form.Label>
                    <Form.Select
                      value={selectedDomain}
                      onChange={(e) => setSelectedDomain(e.target.value)}
                    >
                      <option value="">All Domains</option>
                      {domains.map(domain => (
                        <option key={domain.id} value={domain.domain}>
                          {domain.domain}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Search Email Accounts</Form.Label>
                    <InputGroup>
                      <InputGroup.Text><Search /></InputGroup.Text>
                      <Form.Control
                        type="text"
                        placeholder="Search by email address..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                      />
                    </InputGroup>
                  </Form.Group>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Row>
        <Col>
          <Card>
            <Card.Header>
              <div className="d-flex justify-content-between align-items-center">
                <h5 className="mb-0">Email Accounts ({filteredAccounts.length})</h5>
                <div className="d-flex gap-2">
                  <Button
                    size="sm"
                    variant="outline-primary"
                    onClick={() => setSelectedDomain('')}
                  >
                    <Filter className="me-1" />Clear Filters
                  </Button>
                </div>
              </div>
            </Card.Header>
            <Card.Body className="p-0">
              <Table responsive hover={false} className="mb-0">
                <thead className="table-light">
                  <tr>
                    <th style={{ fontSize: '0.85rem' }}>
                      <Form.Check
                        type="checkbox"
                        checked={selectedAccounts.size === sortedAccounts.length && sortedAccounts.length > 0}
                        onChange={handleSelectAll}
                        aria-label="Select all email accounts"
                      />
                    </th>
                    <th style={{ fontSize: '0.85rem' }}>Profile Picture</th>
                    <th
                      style={{ fontSize: '0.85rem', cursor: 'pointer', userSelect: 'none' }}
                      onClick={() => handleSort('email')}
                      title="Sort by Email Address"
                    >
                      Email Address
                      {sortBy === 'email' ? (sortDir === 'asc' ? <ArrowUp className="ms-1" size={12} /> : <ArrowDown className="ms-1" size={12} />) : <ArrowDownUp className="ms-1 text-muted" size={12} />}
                    </th>
                    <th
                      style={{ fontSize: '0.85rem', cursor: 'pointer', userSelect: 'none' }}
                      onClick={() => handleSort('domain')}
                      title="Sort by Domain"
                    >
                      Domain
                      {sortBy === 'domain' ? (sortDir === 'asc' ? <ArrowUp className="ms-1" size={12} /> : <ArrowDown className="ms-1" size={12} />) : <ArrowDownUp className="ms-1 text-muted" size={12} />}
                    </th>
                    <th
                      style={{ fontSize: '0.85rem', cursor: 'pointer', userSelect: 'none' }}
                      onClick={() => handleSort('status')}
                      title="Sort by Status"
                    >
                      Status
                      {sortBy === 'status' ? (sortDir === 'asc' ? <ArrowUp className="ms-1" size={12} /> : <ArrowDown className="ms-1" size={12} />) : <ArrowDownUp className="ms-1 text-muted" size={12} />}
                    </th>
                    <th
                      style={{ fontSize: '0.85rem', cursor: 'pointer', userSelect: 'none' }}
                      onClick={() => handleSort('sendCount')}
                      title={`Sort by Sent Count (${sendCountYear})`}
                    >
                      Sent Count ({sendCountYear})
                      {sortBy === 'sendCount' ? (sortDir === 'asc' ? <ArrowUp className="ms-1" size={12} /> : <ArrowDown className="ms-1" size={12} />) : <ArrowDownUp className="ms-1 text-muted" size={12} />}
                    </th>
                    <th
                      style={{ fontSize: '0.85rem', cursor: 'pointer', userSelect: 'none' }}
                      onClick={() => handleSort('bounceRate')}
                      title="Sort by Bounce Rate"
                    >
                      Bounce Rate
                      {sortBy === 'bounceRate' ? (sortDir === 'asc' ? <ArrowUp className="ms-1" size={12} /> : <ArrowDown className="ms-1" size={12} />) : <ArrowDownUp className="ms-1 text-muted" size={12} />}
                    </th>
                    <th
                      style={{ fontSize: '0.85rem', cursor: 'pointer', userSelect: 'none' }}
                      onClick={() => handleSort('createdAt')}
                      title="Sort by Created Date"
                    >
                      Created
                      {sortBy === 'createdAt' ? (sortDir === 'asc' ? <ArrowUp className="ms-1" size={12} /> : <ArrowDown className="ms-1" size={12} />) : <ArrowDownUp className="ms-1 text-muted" size={12} />}
                    </th>
                    <th
                      style={{ fontSize: '0.85rem', cursor: 'pointer', userSelect: 'none' }}
                      onClick={() => handleSort('lastUsed')}
                      title="Sort by Last Used"
                    >
                      Last Used
                      {sortBy === 'lastUsed' ? (sortDir === 'asc' ? <ArrowUp className="ms-1" size={12} /> : <ArrowDown className="ms-1" size={12} />) : <ArrowDownUp className="ms-1 text-muted" size={12} />}
                    </th>
                    <th
                      style={{ fontSize: '0.85rem', cursor: 'pointer', userSelect: 'none' }}
                      onClick={() => handleSort('actions')}
                      title="Sort by order"
                    >
                      Actions
                      {sortBy === 'actions' ? (sortDir === 'asc' ? <ArrowUp className="ms-1" size={12} /> : <ArrowDown className="ms-1" size={12} />) : <ArrowDownUp className="ms-1 text-muted" size={12} />}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {sortedAccounts.map((account) => (
                    <tr key={account.id}>
                      <td>
                        <Form.Check
                          type="checkbox"
                          checked={selectedAccounts.has(account.id)}
                          onChange={() => handleSelectAccount(account.id)}
                          aria-label={`Select ${account.email}`}
                        />
                      </td>
                      <td>
                        <div style={{ position: 'relative', width: '40px', height: '40px', minWidth: '40px' }}>
                          {account.avatar && !failedAvatars.has(account.id) ? (
                            <img 
                              src={account.avatar} 
                              alt={account.username}
                              className="rounded-circle"
                              style={{ 
                                width: '40px', 
                                height: '40px', 
                                objectFit: 'cover',
                                display: 'block'
                              }}
                              onError={(e) => {
                                const img = e.currentTarget;
                                console.error('Avatar image failed to load:', account.avatar);
                                console.error('Image src:', img.src);
                                // Mark this avatar as failed
                                setFailedAvatars(prev => new Set(prev).add(account.id));
                              }}
                              onLoad={() => {
                                console.log('Avatar image loaded successfully:', account.avatar);
                              }}
                            />
                          ) : (
                            <div className="rounded-circle d-flex align-items-center justify-content-center bg-primary text-white" style={{ width: '40px', height: '40px' }}>
                              {account.username.charAt(0).toUpperCase()}
                            </div>
                          )}
                        </div>
                      </td>
                      <td>
                        <div style={{ fontSize: '0.9rem' }}>
                          <strong>{account.username}@</strong>
                          {(account as any).hideFromInbox && (
                            <Badge bg="secondary" className="ms-1" style={{ fontSize: '0.65rem', verticalAlign: 'middle' }}>Hidden</Badge>
                          )}
                        </div>
                      </td>
                      <td>
                        <Badge bg="info">{account.domain}</Badge>
                      </td>
                      <td>{getStatusBadge(account)}</td>
                      <td>
                        <span className="fw-bold">{account.sendCount?.toLocaleString() || '0'}</span>
                      </td>
                      <td>
                        <Badge bg={getBounceRateColor(account.bounceRate || 0)}>
                          {account.bounceRate || 0}%
                        </Badge>
                      </td>
                      <td>
                        {account.createdAt
                          ? toLocaleDateStringInTimezone(account.createdAt, undefined, undefined, tenantTimezone)
                          : '—'}
                      </td>
                      <td>
                        {account.lastUsed
                          ? toLocaleDateStringInTimezone(account.lastUsed, undefined, undefined, tenantTimezone)
                          : 'Never'}
                      </td>
                      <td style={{ position: 'relative', zIndex: 1 }}>
                        <div 
                          className="d-flex gap-1 align-items-center" 
                          style={{ flexWrap: 'nowrap', position: 'relative', zIndex: 1 }}
                          onClick={(e) => {
                            // Prevent row clicks from interfering with button clicks
                            if ((e.target as HTMLElement).closest('button, .btn')) {
                              e.stopPropagation();
                            }
                          }}
                        >
                          {/* Outbound status: Ready to Send vs Configure in Domains (no SES verification) */}
                          {getOutboundStatusBadge(account)}
                          <Button 
                            size="sm" 
                            variant="outline-danger"
                            onClick={() => handleDeleteAccount(account.id)}
                            className="btn-sm"
                            style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem', whiteSpace: 'nowrap' }}
                            title="Delete Email Account"
                          >
                            <Trash size={14} />
                          </Button>
                          <Button 
                            size="sm" 
                            variant="outline-secondary"
                            onClick={() => handleSettings(account.id)}
                            className="btn-sm"
                            style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem', whiteSpace: 'nowrap' }}
                            title="Account Settings"
                          >
                            <Gear size={14} />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {filteredAccounts.length === 0 && (
        <Row>
          <Col>
            <Card className="text-center py-5">
              <Card.Body>
                <Envelope size={48} className="text-muted mb-3" />
                <h5>No email accounts found</h5>
                <p className="text-muted">
                  {selectedDomain || searchTerm 
                    ? 'Try adjusting your filters or search terms'
                    : 'Add your first email account to start sending emails'
                  }
                </p>
                <Button variant="primary" onClick={handleAddAccount}>
                  <Plus className="me-1" />Add Account
                </Button>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      )}

      {/* Add Email Account Modal */}
      <AddEmailAccount
        show={showAddModal}
        onHide={() => setShowAddModal(false)}
        onSuccess={handleAddSuccess}
      />

      {/* Settings Modal */}
      <Modal
        show={showSettingsModal}
        onHide={closeSettingsModal}
        backdrop={isSavingSettings ? 'static' : true}
        keyboard={!isSavingSettings}
        size="lg"
        centered
      >
        <Modal.Header closeButton={!isSavingSettings}>
          <Modal.Title>Email Account Settings</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {editingAccount && (
            <>
              <div className="mb-4">
                <h6 className="mb-3">Account Information</h6>
                <div className="mb-2"><strong>Email:</strong> {editingAccount.email}</div>
                <div className="mb-2"><strong>Domain:</strong> {editingAccount.domain}</div>
                <div className="mb-2">
                  <strong>Status:</strong>{' '}
                  {isAccountReadyToSend(editingAccount) ? (
                    <Badge bg="primary">
                      <CheckCircle size={12} className="me-1" />
                      Active
                    </Badge>
                  ) : (
                    <Badge bg="warning">Pending</Badge>
                  )}
                </div>
              </div>
              
              <div className="mb-4">
                <h6 className="mb-3">Profile Picture</h6>
                <div className="d-flex align-items-center mb-3">
                  <div className="me-3">
                    {avatarPreview ? (
                      <img 
                        src={avatarPreview} 
                        alt={editingAccount.email}
                        className="rounded-circle"
                        style={{ width: '80px', height: '80px', objectFit: 'cover' }}
                      />
                    ) : (
                      <div 
                        className="rounded-circle bg-primary text-white d-flex align-items-center justify-content-center"
                        style={{ width: '80px', height: '80px', fontSize: '32px', fontWeight: 'bold' }}
                      >
                        {editingAccount.username.charAt(0).toUpperCase()}
                      </div>
                    )}
                  </div>
                  <div>
                    <Form.Group>
                      <Form.Label>Upload Profile Picture</Form.Label>
                      <Form.Control
                        type="file"
                        accept="image/*"
                        onChange={handleAvatarChange}
                        disabled={isSavingSettings}
                      />
                      <Form.Text className="text-muted">
                        Recommended: Square image, max 5MB. Formats: JPG, PNG, GIF
                      </Form.Text>
                    </Form.Group>
                  </div>
                </div>
                {avatarPreview && (
                  <Button 
                    variant="outline-danger" 
                    size="sm"
                    disabled={isSavingSettings}
                    onClick={() => {
                      setAvatarPreview(null);
                      setAvatarFile(null);
                    }}
                  >
                    Remove Picture
                  </Button>
                )}
              </div>
              
              <div className="mb-3">
                <h6 className="mb-3">Account Statistics</h6>
                <div className="mb-2"><strong>Sent Count ({sendCountYear}):</strong> {editingAccount.sendCount?.toLocaleString() || '0'}</div>
                <div className="mb-2"><strong>Bounce Rate:</strong> {editingAccount.bounceRate || 0}%</div>
                <div className="mb-2">
                  <strong>Last Used:</strong>{' '}
                  {editingAccount.lastUsed
                    ? toLocaleDateStringInTimezone(editingAccount.lastUsed, undefined, undefined, tenantTimezone)
                    : 'Never'}
                </div>
              </div>
              
              <div className="mb-4">
                <h6 className="mb-3">Inbox Visibility</h6>
                <Form.Check
                  type="switch"
                  id="hide-from-inbox-toggle"
                  label="Hide from All Inboxes"
                  checked={hideFromInbox}
                  disabled={isSavingSettings}
                  onChange={(e) => setHideFromInbox(e.target.checked)}
                />
                <Form.Text className="text-muted">
                  When enabled, emails to this account won't appear in the "All Inboxes" view. The account remains accessible from the sidebar.
                </Form.Text>
                <Form.Check
                  className="mt-3"
                  type="switch"
                  id="hide-from-inbox-count-toggle"
                  label="Hide from Inbox email count"
                  checked={hideFromInboxCount}
                  disabled={isSavingSettings}
                  onChange={(e) => setHideFromInboxCount(e.target.checked)}
                />
                <Form.Text className="text-muted">
                  When enabled, emails from this account are excluded from the Unibox Inbox total count.
                </Form.Text>
                {hideFromInbox && editingAccount.email.startsWith('dmarc@') && (
                  <Alert variant="info" className="mt-2 py-1 small mb-0">
                    Recommended for DMARC collection inboxes to keep your feed clean.
                  </Alert>
                )}
              </div>

              <EmailAccountDNSSection domain={editingAccount.domain} email={editingAccount.email} />
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={closeSettingsModal} disabled={isSavingSettings}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleSaveSettings}
            disabled={isSavingSettings}
          >
            {isSavingSettings ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" role="status" aria-hidden="true" />
                Saving…
              </>
            ) : (
              'Save Settings'
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            <Trash className="me-2 text-danger" />
            Confirm Delete
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {deleteMode === 'single' ? (
            <p>Are you sure you want to delete this email account?</p>
          ) : (
            <p>Are you sure you want to delete <strong>{selectedAccounts.size}</strong> selected email account(s)?</p>
          )}
          <p className="text-muted small">This action cannot be undone.</p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={confirmDelete} disabled={isDeleting}>
            <Trash className="me-1" />
            {isDeleting ? 'Deleting...' : `Delete ${deleteMode === 'single' ? 'Account' : `${selectedAccounts.size} Accounts`}`}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Help Sidebar */}
      <Offcanvas
        show={showHelp}
        onHide={() => setShowHelp(false)}
        placement="end"
        style={{ width: '400px' }}
      >
        <Offcanvas.Header closeButton className="border-bottom">
          <Offcanvas.Title className="d-flex align-items-center">
            <QuestionCircle className="me-2 text-primary" size={20} />
            <span className="fw-bold">Outbound Status</span>
          </Offcanvas.Title>
        </Offcanvas.Header>
        <Offcanvas.Body>
          <div>
            <h6 className="mb-3">Ready to Send vs Configure in Domains</h6>
            <p className="mb-3">
              KG does <strong>not</strong> use AWS SES for outbound. Sending is governed by:
            </p>
            <ul className="mb-3">
              <li><strong>DNS</strong> — SPF, DKIM, DMARC in Route 53 (Domains → Auth)</li>
              <li><strong>SMTP relay credentials</strong> — Channel login + password for SMTP.com (or other provider) in <strong>Domains → Outbound</strong></li>
            </ul>
            <p className="mb-3">
              <strong>Ready to Send</strong> means the account’s domain has DNS and relay credentials in place. <strong>Configure in Domains</strong> means set DNS and/or SMTP.com credentials in Domains → Outbound. There is no sender “verification” step.
            </p>
            <div className="alert alert-info mb-0">
              Inbound: Postmark. Outbound: kg-smtp submission then relay (SMTP.com / MailChannels / Mailgun). No SES.
            </div>
          </div>
        </Offcanvas.Body>
      </Offcanvas>

      {/* Toast Notifications */}
      <ToastContainer position="top-end" className="p-3" style={{ zIndex: 9999 }}>
        <Toast
          show={showSuccessToast}
          onClose={() => setShowSuccessToast(false)}
          delay={3000}
          autohide
          bg="success"
        >
          <Toast.Header>
            <strong className="me-auto">Success</strong>
          </Toast.Header>
          <Toast.Body className="text-white">
            {toastMessage || 'Email accounts refreshed successfully'}
          </Toast.Body>
        </Toast>
        
        <Toast
          show={showErrorToast}
          onClose={() => setShowErrorToast(false)}
          delay={5000}
          autohide
          bg="danger"
        >
          <Toast.Header>
            <strong className="me-auto">Error</strong>
          </Toast.Header>
          <Toast.Body className="text-white">
            {toastMessage || 'Failed to refresh email accounts'}
          </Toast.Body>
        </Toast>
      </ToastContainer>
    </Container>
    </>
  );
};

export default EmailAccounts;
